import { useWindowOpen } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, Drawer, DrawerBody, DrawerContent, DrawerFooter, LottiePlayer, ModalStickyBottomWrapper, TextButton } from '@repo/ui';
import { useEffect, useState } from 'react';
import { useMount } from 'react-use';

import { AD_SLOT_RESOURCE_CENTER_URL, SlotTargetType } from '@/lib/constants/ad-slots';
import { useAdSlots } from '@/lib/hooks/use-ad-slots';
import { BusinessType } from '@/lib/types/request';

type DrawerOptionsType = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  delay?: number;
  eventAttributes?: Record<string, string | number>;
};

export const LookupResultAdDrawer = ({ open, onOpenChange, delay, businessType, eventAttributes }: { businessType: BusinessType } & DrawerOptionsType) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [taxpayerType, setTaxpayerType] = useState<SlotTargetType | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!businessType) return;
    if (!isHydrated) return;
    const isPersonalTritx = sessionStorage.getItem('isPersonalTritx');
    if (isPersonalTritx === 'true') {
      setTaxpayerType('PERSONAL');
    } else {
      setTaxpayerType(businessType.toUpperCase() as SlotTargetType);
    }
  }, [isHydrated, businessType]);

  if (!taxpayerType) return null;
  return <AdBottomDrawer taxpayerType={taxpayerType} open={open} onOpenChange={onOpenChange} delay={delay} eventAttributes={eventAttributes} />;
};

const AdBottomDrawer = ({
  taxpayerType,
  open,
  onOpenChange,
  delay = 1500,
  eventAttributes: additionalEventAttributes
}: {
  taxpayerType: SlotTargetType;
} & DrawerOptionsType) => {
  const { slots, labItemIdx, replaceNewLine } = useAdSlots('LOOKUP_RESULT', taxpayerType);

  const slot = slots?.[0];
  const item = slots?.[0]?.items?.[labItemIdx];
  const { sendViewEvent, sendClickEvent } = useUserEventLogger();
  const { openWindow } = useWindowOpen();

  const newItem = {
    eventName: item?.userEvent ?? '',
    linkUrl: item?.linkUrl ?? '',
    ctaButtonLabel: item?.buttonText?.replace(/>/g, '$>;') ?? '',
    title: replaceNewLine(item?.displayTitle),
    description: replaceNewLine(item?.description),
    imageUrl: item?.imagePath ? `${AD_SLOT_RESOURCE_CENTER_URL}/${item.imagePath}` : '',
    labId: item?.displayCode ?? '',
    labType: slot?.title ?? ''
  };

  const { ctaButtonLabel, labType, labId, linkUrl, description, imageUrl, title, eventName } = newItem;

  const eventAttributes = {
    'refund.lab.type': labType ?? '',
    'refund.lab.id': labId ?? '',
    ...additionalEventAttributes
  };

  sendViewEvent(eventName, eventAttributes, { isAllowRetry: true });

  useMount(() => {
    const timeout = setTimeout(() => onOpenChange(true), delay ?? 0);
    return () => clearTimeout(timeout);
  });

  if (labItemIdx === -1) return null;
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent disableOutsideClick={true}>
        <DrawerBody>
          <div className={'flex flex-col items-center gap-y-[15px] pt-12 pb-[13.5px]'}>
            {imageUrl.toLowerCase().endsWith('json') ? (
              <div className={'w-[120px] h-[120px]'}>
                <LottiePlayer src={imageUrl} loop={true} autoPlay={true} />
              </div>
            ) : (
              <img src={imageUrl} alt={title} width={120} height={120} />
            )}
            <div className={'flex flex-col items-center gap-y-[7.5px] text-center'}>
              <p className={'text-neutral-foreground-main text-[18.7px] font-bold leading-[26.5px] whitespace-pre-line'}>{title}</p>
              <p className={'text-neutral-foreground-low text-[15px] leading-[21.7px] whitespace-pre-line'}>{description}</p>
            </div>
          </div>
        </DrawerBody>
        <DrawerFooter>
          <ModalStickyBottomWrapper align={'vertical'}>
            <BoxButton
              size={'large'}
              className={'w-full'}
              variant={'filled-primary'}
              onClick={() => {
                sendClickEvent(`${eventName}_ok`, eventAttributes, { isAllowRetry: true });
                openWindow(linkUrl, '_system');
              }}
            >
              {ctaButtonLabel}
            </BoxButton>
            <TextButton
              size={'medium'}
              className={'w-full'}
              variant={'primary'}
              onClick={() => {
                sendClickEvent(`${eventName}_close`, eventAttributes, { isAllowRetry: true });
                onOpenChange(false);
              }}
            >
              닫기
            </TextButton>
          </ModalStickyBottomWrapper>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
